<script setup lang="ts">
  import { computed } from "vue";
  import { Layers2, Star, Trash2 } from "lucide-vue-next";
  import { storeToRefs } from "pinia";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { IndexTable, IndexTableEmpty } from "@/components/episto-ui/index-table";
  import type { DataTableColumn } from "@/components/episto-ui/data-table";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import { useCurrentUserStore } from "@stores/currentUser.js";
  import { useGridRowsStore } from "@stores/survey/grids/gridRows";
  import { useGridsStore } from "@stores/survey/grids/grids";
  import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox.js";
  import GridAnswerCell from "./GridAnswerCell.vue";
  import GridColumnMenu from "./GridColumnMenu.vue";
  import { columnKey, SEPARATED, type GridTableColumn } from "./gridColumns";
  import { useInterviewValues } from "./useInterviewValues";
  import type { Interview } from "@/types";

  const props = withDefaults(
    defineProps<{
      interviews: Interview[];
      columns?: GridTableColumn[];
      selectedInterviewId?: number | null;
      loading?: boolean;
      canAddRows?: boolean;
      currentPage?: number;
      total?: number;
      pageSize?: number;
    }>(),
    { columns: () => [], selectedInterviewId: null, loading: false, canAddRows: false, currentPage: 1, total: 0, pageSize: 25 },
  );

  const emit = defineEmits<{
    select: [interview: Interview];
    "select-conversation": [interview: Interview, messageId: string | number];
    "select-answer": [interview: Interview, column: GridTableColumn];
    "toggle-favorite": [id: number];
    "edit-recode": [column: GridTableColumn];
    "reorder-columns": [columns: GridTableColumn[]];
    "reorder-rows": [interviews: Interview[]];
    "remove-column": [column: GridTableColumn];
    "remove-row": [interview: Interview];
    "page-change": [page: number];
  }>();

  const { isAdmin } = storeToRefs(useCurrentUserStore());
  const { columnLabel } = useGridRowsStore();
  const { cellFor } = useGridsStore();
  const { columnValue, justificationMessageId, translatedQuote } = useInterviewValues();

  const label = (column: GridTableColumn) =>
    column.kind === "ai_question" ? column.prompt : column.option ? columnLabel(column.option) : "";

  const tableColumns = computed<DataTableColumn<Interview>[]>(() => [
    { key: "token", label: "ID", width: 100 },
    { key: "quick_take", label: "Quick take", width: 320 },
    ...props.columns.map((column) => ({ key: columnKey(column), label: label(column), width: 220, reorderable: true, ...SEPARATED })),
  ]);

  const onReorderColumns = (keys: string[]) =>
    emit(
      "reorder-columns",
      keys.flatMap((key) => props.columns.filter((column) => columnKey(column) === key)),
    );
</script>

<template>
  <IndexTable
    :items="interviews"
    :columns="tableColumns"
    :current-page="currentPage"
    :total="total"
    :page-size="pageSize"
    :loading="loading"
    :selected-key="selectedInterviewId"
    :skeleton-rows="25"
    actions-class="w-[88px]"
    resizable
    compact
    reorderable-columns
    :reorderable-rows="canAddRows"
    empty-message="No respondents found."
    @row-click="(interview) => emit('select', interview)"
    @page-change="emit('page-change', $event)"
    @reorder-columns="onReorderColumns"
    @reorder-rows="emit('reorder-rows', $event)">
    <template
      v-for="column in columns"
      :key="`header-${columnKey(column)}`"
      #[`header-${columnKey(column)}-actions`]>
      <AiStar
        v-if="column.kind === 'ai_question'"
        class="size-3.5 shrink-0 text-primary" />
      <Layers2
        v-else-if="column.option?.is_for_analytics"
        class="size-3.5 shrink-0 text-muted-foreground" />
      <GridColumnMenu
        :editable="!!column.option?.is_for_analytics"
        @edit="emit('edit-recode', column)"
        @remove="emit('remove-column', column)" />
    </template>

    <template #cell-token="{ item }">
      <div class="flex flex-col">
        <span class="font-medium text-primary">#{{ item.token }}</span>
        <span
          v-if="isAdmin"
          class="text-foreground-subtle tabular-nums">
          #{{ item.id }}
        </span>
      </div>
    </template>

    <template #cell-quick_take="{ item }">
      <span
        v-if="item.quick_take"
        class="block truncate"
        :title="removeHtmlMarkdownAndLineBreak(item.quick_take as string)">
        {{ removeHtmlMarkdownAndLineBreak(item.quick_take as string) }}
      </span>
      <IndexTableEmpty v-else />
    </template>

    <template
      v-for="column in columns"
      :key="columnKey(column)"
      #[`cell-${columnKey(column)}`]="{ item }">
      <GridAnswerCell
        v-if="column.kind === 'ai_question'"
        :cell="cellFor(column.id, item.id)"
        @select="emit('select-answer', item, column)" />
      <template v-else-if="column.option && columnValue(item, column.option)">
        <Tooltip
          v-if="justificationMessageId(item, column.option)"
          :delay-duration="200"
          :content="translatedQuote(item, justificationMessageId(item, column.option)) ?? ''">
          <span
            class="block cursor-pointer truncate underline decoration-border-strong decoration-dashed underline-offset-4"
            @click.stop="emit('select-conversation', item, justificationMessageId(item, column.option) as string | number)">
            {{ columnValue(item, column.option) }}
          </span>
        </Tooltip>
        <span
          v-else
          class="block truncate">
          {{ columnValue(item, column.option) }}
        </span>
      </template>
      <IndexTableEmpty v-else />
    </template>

    <template #actions="{ item }">
      <div class="flex items-center justify-end">
        <Button
          variant="ghost"
          size="icon-sm"
          :class="item.favorite ? 'text-status-warning' : 'text-foreground-subtle hover:text-status-warning'"
          :aria-label="item.favorite ? 'Remove from favorites' : 'Add to favorites'"
          @click.stop="emit('toggle-favorite', item.id)">
          <Star
            class="size-4"
            :class="item.favorite ? 'fill-current' : ''" />
        </Button>
        <Button
          v-if="canAddRows"
          variant="ghost"
          size="icon-sm"
          class="text-foreground-subtle hover:text-destructive"
          aria-label="Remove from grid"
          @click.stop="emit('remove-row', item)">
          <Trash2 class="size-4" />
        </Button>
      </div>
    </template>
  </IndexTable>
</template>
